import { useEffect, useMemo, useState } from 'react';
import { TELEMETRY, resolveGroupKey, readMetric } from '../data/telemetry';

const TICK_MS = 1500;

/**
 * Telemetry for the asset on the home stage and the part selected on it.
 *
 * Returns:
 *   bar      — metrics for the app bar: the selected part's group when it has
 *              telemetry (first three readings), otherwise the asset's bar set;
 *   barScope — 'asset' | 'part', and barTitle for the part case;
 *   group    — { key, title, metrics } for the selected part, or null;
 *   hasPartSelection — a part is selected (group may still be null);
 *   location, alerts — asset context for the bar;
 *   keyReadings — the asset's key readings for the voice assistant (def.voiceKey), or null.
 */
export default function useTelemetry(assetId, selectedId, vehicleData) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const def = TELEMETRY[assetId] || null;
  const groupKey = useMemo(() => resolveGroupKey(assetId, selectedId), [assetId, selectedId]);

  if (!def) return null;

  const ctx = { vehicleData };
  const read = (metrics) => metrics.map((metric) => readMetric(metric, ctx, tick));

  const groupDef = groupKey ? def.groups[groupKey] : null;
  const group = groupDef ? { key: groupKey, title: groupDef.title, metrics: read(groupDef.metrics) } : null;

  return {
    bar: group ? group.metrics.slice(0, 3) : read(def.bar),
    barScope: group ? 'part' : 'asset',
    barTitle: group ? group.title : null,
    group,
    hasPartSelection: Boolean(selectedId),
    location: def.location(ctx),
    alerts: def.alerts(ctx),
    keyReadings: def.voiceKey ? read(def.voiceKey) : null,
  };
}
